import moment from 'moment'
import React, { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import { EuiButton, EuiFlexGroup, EuiFlexItem, EuiFlyoutBody, EuiFlyoutFooter, EuiFlyoutHeader, EuiFormRow, EuiSpacer, EuiTextArea, EuiTitle } from '@elastic/eui'
import AcFlyout from 'components/AcFlyout'

import { IntervalUnit, LocationPostalCode, PostalCodeTarget, SegmentTarget } from 'api/interfaces'
import { AIPlanCampaignBuilderType } from 'api/interfaces/AIPlan'
import { MasterCampaignPlanCampaignBuilderType } from 'api/interfaces/MasterCampaignStub'
import { useGetAIPlanStubQuery, useEditAIPlanStubCampaignBuilderMutation } from 'api/rtkQueryApi/platform/aiPlansApi'
import { AcTargeterSelection } from 'components/targeting/finders/AcTargeterSelection'
import { PostalCodeFinder } from 'components/targeting/finders/PostalCodeFinder'
import { SegmentsForm } from 'components/targeting/TargetingForm/SegmentsForm'
import { CampaignType } from 'utils/CampaignType'

export type CampaignSectionType = 'summary' | 'timing' | 'geo' | 'audience' | 'keywords'

export interface EditAIPlanCampaignSectionFlyoutProps {
  campaignBuilderId: string
  sectionType: CampaignSectionType
  onClose: () => void
}

const sectionLabels: Record<CampaignSectionType, string> = {
  summary: 'Summary',
  timing: 'Timing',
  geo: 'Geography',
  audience: 'Audience Segments',
  keywords: 'Keywords'
}

const sectionTextFields: Record<CampaignSectionType, string> = {
  summary: 'longSummary',
  timing: 'seasonalitySummary',
  geo: 'geoSummary',
  audience: 'audienceSummary',
  keywords: 'keywordDescription'
}

const EditAIPlanCampaignSectionFlyout: React.FC<EditAIPlanCampaignSectionFlyoutProps> = ({ campaignBuilderId, sectionType, onClose }) => {
  const { aiPlanId } = useParams<{ aiPlanId: string }>()
  const { data: stub } = useGetAIPlanStubQuery({ aiPlanId }, { skip: !aiPlanId })
  const didFireUpdate = useRef(false)

  const builder = stub?.campaignBuilders.find(b => b.id === campaignBuilderId)

  const getInitialText = (): string => {
    if (!builder) return ''
    return (builder[sectionTextFields[sectionType] as keyof typeof builder] as string) || ''
  }

  const [sectionText, setSectionText] = useState(getInitialText)
  const [zips, setZips] = useState<PostalCodeTarget[]>(builder?.zips || [])
  const [segmentTargets, setSegmentTargets] = useState<SegmentTarget[]>(builder?.segmentTargets || [])

  const [editCampaignBuilder, { isSuccess, isLoading }] = useEditAIPlanStubCampaignBuilderMutation()

  useEffect(() => {
    if (didFireUpdate.current && isSuccess) {
      didFireUpdate.current = false
      onClose()
    }
  }, [didFireUpdate, isSuccess, onClose])

  if (!builder || !stub) return null

  const isBillboard =
    builder.campaignType === AIPlanCampaignBuilderType.Billboard ||
    (builder.campaignType as unknown as MasterCampaignPlanCampaignBuilderType) === MasterCampaignPlanCampaignBuilderType.Billboard

  const showZips = sectionType === 'geo' && !isBillboard
  const showSegments = sectionType === 'audience' && !isBillboard && (
    (builder.campaignType as unknown as MasterCampaignPlanCampaignBuilderType) === MasterCampaignPlanCampaignBuilderType.Internet ||
    (builder.campaignType as unknown as MasterCampaignPlanCampaignBuilderType) === MasterCampaignPlanCampaignBuilderType.TV
  )

  const onSave = () => {
    const request = {
      name: builder.name,
      shortSummary: builder.shortSummary,
      longSummary: sectionType === 'summary' ? sectionText : builder.longSummary,
      audienceSummary: sectionType === 'audience' ? sectionText : builder.audienceSummary,
      audienceDetails: builder.audienceDetails || '',
      geoSummary: sectionType === 'geo' ? sectionText : builder.geoSummary,
      geoDetails: builder.geoDetails || '',
      seasonalitySummary: sectionType === 'timing' ? sectionText : builder.seasonalitySummary,
      seasonalityDetails: builder.seasonalityDetails || '',
      campaignType: builder.campaignType as unknown as string,
      isOffAdCritter: builder.isOffAdCritter,
      offAdCritterName: builder.offAdCritterName,
      offAdCritterColor: builder.offAdCritterColor,
      intervalBudget: builder.intervalBudget,
      retargetSite: builder.retargetSite,
      isTotalBudget: builder.isTotalBudget,
      startDate: moment(builder.startDate).toISOString(),
      endDate: builder.endDate ? moment(builder.endDate).toISOString() : undefined,
      intervalUnit: (builder.intervalUnit || IntervalUnit.Month) as unknown as string,
      intervalFrequency: builder.intervalFrequency || 1,
      intervalDuration: builder.intervalDuration || 1,
      segmentTargets: sectionType === 'audience' ? segmentTargets : (builder.segmentTargets || []),
      audienceSegmentLooksUp: builder.audienceSegmentLooksUp || [],
      targetingType: builder.targetingType as unknown as string | undefined,
      regions: builder.regions || [],
      dmas: builder.dmas || [],
      cities: builder.cities || [],
      zips: sectionType === 'geo' ? zips : (builder.zips || []),
      keywords: builder.keywords || [],
      keywordDescription: builder.keywordDescription || '',
      segmentDescriptions: builder.segmentDescriptions || [],
      includeInProjections: builder.includeInProjections || false,
      includeInGeoHeatmap: builder.includeInGeoHeatmap || false
    }

    editCampaignBuilder({
      aiPlanStubId: stub.id,
      campaignBuilderId,
      aiPlanId,
      request
    })
    didFireUpdate.current = true
  }

  return (
    <AcFlyout onClose={onClose} size='m'>
      <EuiFlyoutHeader>
        <EuiTitle size='m'>
          <h2>Edit {sectionLabels[sectionType]}</h2>
        </EuiTitle>
      </EuiFlyoutHeader>
      <EuiFlyoutBody>
        <EuiFormRow fullWidth label={sectionLabels[sectionType]}>
          <EuiTextArea fullWidth value={sectionText} onChange={e => setSectionText(e.target.value)} rows={15} />
        </EuiFormRow>

        {showZips && (
          <>
            <EuiSpacer size='m' />
            <EuiFormRow fullWidth label='Zip Codes'>
              <PostalCodeFinder
                onOptionClick={(zip: LocationPostalCode) => setZips([...zips, zip])}
              />
            </EuiFormRow>
            <EuiFormRow fullWidth>
              <AcTargeterSelection
                items={zips.map(r => ({ key: r.id.toString(), label: r.name }))}
                onItemRemoved={item => setZips(zips.filter(r => r.id.toString() !== item.key))}
              />
            </EuiFormRow>
          </>
        )}

        {showSegments && (
          <>
            <EuiSpacer size='m' />
            <EuiFormRow fullWidth label='Audience Segments'>
              <SegmentsForm
                campaignType={builder.campaignType as unknown as CampaignType}
                segments={segmentTargets}
                onSegmentsChange={setSegmentTargets}
              />
            </EuiFormRow>
          </>
        )}
      </EuiFlyoutBody>
      <EuiFlyoutFooter>
        <EuiFlexGroup direction='row' alignItems='center'>
          <EuiFlexItem grow={false}>
            <EuiButton onClick={onClose}>Cancel</EuiButton>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiButton fill onClick={onSave} isLoading={isLoading}>
              Save
            </EuiButton>
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiFlyoutFooter>
    </AcFlyout>
  )
}

export default EditAIPlanCampaignSectionFlyout
